import L from 'leaflet';
import { find, isNil } from 'lodash';
import turf from '@turf/turf';

/**
 * Adds a new leaflet marker to a leaflet layer group.
 *
 * @param {object} arg - The arguments object.
 * @param {object} arg.layerMarkers - The leaflet layer group to add the marker.
 * @param {object} arg.item - A Feature Point to create the marker.
 * @param {string | number} arg.layerId - The id of the marker layer in the group.
 * @param {object} arg.style - The style for the marker.
 * @param {boolean} [arg.circleMarker] - Flag to use leaflet CircleMarker class.
 * @param {boolean} [arg.hasPopup] - Flag to add a popup to the marker.
 * @param {string} [arg.popupContent] - The popup content.
 * @param {boolean} [arg.clickable] - Flag to add onclick event listener.
 * @param {Function} [arg.onClick] - The function for onclick event.
 * @param {boolean} [arg.hoverable] - Flag to add onmouseover and onmouseout event listeners.
 * @param {boolean} [arg.defaultHover] - Flag to use the default mouseover and mouseout functions.
 * @param {Function} [arg.onMouseOver] - The function the onmouseover event.
 * @param {Function} [arg.onMouseOut] - The function the onmouseout event.
 */
const addMarkerToLayer = ({
  layerMarkers,
  item,
  layerId,
  style,
  circleMarker = false,
  hasPopup = false,
  popupContent = '',
  clickable = false,
  onClick = () => {},
  hoverable = false,
  defaultHover = true,
  onMouseOver = () => {},
  onMouseOut = () => {}
}) => {
  const layers = layerMarkers.getLayers();
  let layer = find(layers, layerToCheck => layerToCheck.layerId === layerId);

  if (isNil(layer)) {
    const [lng, lat] = turf.getCoord(item);
    if (circleMarker) {
      layer = new L.CircleMarker(L.latLng(lat, lng), style);
    } else {
      layer = L.marker(L.latLng(lat, lng), style);
    }
    layer.layerId = layerId;

    if (hasPopup) {
      layer.bindPopup(`${popupContent}`);
    }
    if (clickable) {
      layer.on('click', onClick);
    }
    if (hoverable) {
      let onMouseOverFunction = () => layer.openPopup();
      let onMouseOutFunction = () => layer.closePopup();
      if (!defaultHover) {
        onMouseOverFunction = onMouseOver;
        onMouseOutFunction = onMouseOut;
      }
      layer.on('mouseover', onMouseOverFunction);
      layer.on('mouseout', onMouseOutFunction);
    }

    layerMarkers.addLayer(layer);
  }
};

export default addMarkerToLayer;
